#!/usr/bin/env node
/**
 * Claude Orchestra CLI
 *
 * Unified command-line interface for context manager operations
 * Usage: npx claude-orchestra <command> [args...]
 */

import {
  AgentContextHub,
  TechnologyDetector,
  type Task,
  type AgentArtifact,
} from '../.claude/lib/context-manager';
import { runInit, runUpdate } from './init';

// ===========================
// CLI Helper Functions
// ===========================

function showHelp() {
  console.log(`
Claude Orchestra CLI
====================

Usage: claude-orchestra <command> [args...]

Commands:

  init [--force]                         Initialize Claude Orchestra in current project
  update                                 Update Claude Orchestra to latest version
  status                                 Show comprehensive orchestration status
  next                                   Show next actionable tasks (for resume)
  detect                                 Detect project technology stack
  task <task-id>                         Show detailed task information
  conflicts                              Detect integration conflicts
  register <desc> <agent> [deps...]      Register a new task
  update-task <task-id> <status> [art]   Update task status
  can-start <task-id>                    Check if task can start
  help                                   Show this help message

Examples:

  claude-orchestra init
  claude-orchestra update
  claude-orchestra init --force
  claude-orchestra status
  claude-orchestra detect
  claude-orchestra task task-123
  claude-orchestra register "Build API" backend-specialist

For more information, visit: https://github.com/crs/claude-orchestra
  `);
}

function showStatus() {
  const hub = new AgentContextHub();
  const status = hub.getOrchestrationStatus();

  console.log('\n📊 Orchestration Status');
  console.log('='.repeat(50));

  // Progress overview
  const { progress } = status;
  console.log(
    `\nProgress: ${progress.byStatus.completed}/${progress.totalTasks} tasks completed (${progress.completionRate.toFixed(1)}%)`
  );

  // Tasks by status
  console.log('\nTasks by Status:');
  console.log(`  ✅ Completed: ${progress.byStatus.completed}`);
  console.log(`  ⚙️  In Progress: ${progress.byStatus.in_progress}`);
  console.log(`  ⏳ Pending: ${progress.byStatus.pending}`);
  console.log(`  ⚠️  Blocked: ${progress.byStatus.blocked}`);

  // Active agents
  if (progress.activeAgents.length > 0) {
    console.log('\nActive Agents:');
    progress.activeAgents.forEach((agent) => {
      const tasks = status.tasks.filter(
        (t) => t.assignedTo === agent && t.status === 'in_progress'
      );
      tasks.forEach((task) => {
        console.log(`  - ${agent}: ${task.description}`);
      });
    });
  }

  // Conflicts
  if (status.conflicts.length > 0) {
    console.log('\n⚠️  Conflicts Detected:');
    status.conflicts.forEach((conflict) => {
      console.log(`  [${conflict.severity}] ${conflict.description}`);
    });
  }

  // Next actions
  const nextTasks = status.tasks.filter((t) => {
    if (t.status !== 'pending') return false;
    return t.dependencies.every((depId) => {
      const dep = status.tasks.find((task) => task.taskId === depId);
      return dep?.status === 'completed';
    });
  });

  if (nextTasks.length > 0) {
    console.log('\n🚀 Next Actionable Tasks:');
    nextTasks.forEach((task) => {
      console.log(`  - ${task.taskId}: ${task.description}`);
      console.log(`    Assigned to: ${task.assignedTo}`);
    });
  }

  console.log('');
}

function showNext() {
  const hub = new AgentContextHub();
  const resumable = hub.getResumableOrchestration();

  if (!resumable.hasState) {
    console.log('ℹ️  No active orchestration found');
    return;
  }

  console.log('\n🚀 Next Actionable Tasks:');
  console.log('='.repeat(50));

  resumable.nextTasks.forEach((task, index) => {
    console.log(`\n${index + 1}. ${task.description}`);
    console.log(`   Task ID: ${task.taskId}`);
    console.log(`   Assigned to: ${task.assignedTo}`);
    console.log(`   Estimated tokens: ${task.estimatedContextTokens}`);
  });

  if (resumable.blockedTasks.length > 0) {
    console.log('\n⏳ Blocked Tasks:');
    resumable.blockedTasks.forEach(({ task, blockedBy }) => {
      console.log(`\n   ${task.description}`);
      console.log(`   Waiting on: ${blockedBy.join(', ')}`);
    });
  }

  console.log('');
}

async function detectTech() {
  const detector = new TechnologyDetector();
  const stack = await detector.detectStack();

  console.log('\n🔍 Technology Stack Detection');
  console.log('='.repeat(50));

  if (stack.language === 'unknown') {
    console.log('\n❌ No recognized technology stack found');
    console.log('   Supported: Python, Node.js, Go, Rust, Java');
    return;
  }

  console.log(`\n✅ Detected: ${stack.language.toUpperCase()}`);
  console.log(`   Package Manager: ${stack.packageManager}`);
  if (stack.framework) {
    console.log(`   Framework: ${stack.framework}`);
  }
  console.log(`   Project Files: ${stack.projectFiles.join(', ')}`);

  console.log('\n📋 Suggested Agents:');
  stack.suggestedAgents.forEach((agent) => {
    console.log(`   - ${agent}`);
  });

  console.log('');
}

function showTask(taskId: string) {
  const hub = new AgentContextHub();
  const details = hub.getTaskDetails(taskId);

  if (!details.task) {
    console.log(`❌ Task ${taskId} not found`);
    return;
  }

  const task = details.task;

  console.log('\n📝 Task Details');
  console.log('='.repeat(50));
  console.log(`\nTask ID: ${task.taskId}`);
  console.log(`Description: ${task.description}`);
  console.log(`Assigned to: ${task.assignedTo}`);
  console.log(`Status: ${task.status}`);
  // Handle Date objects that may have been deserialized as strings
  const createdAt = task.createdAt instanceof Date ? task.createdAt : new Date(task.createdAt);
  console.log(`Created: ${createdAt.toISOString()}`);
  if (task.completedAt) {
    const completedAt =
      task.completedAt instanceof Date ? task.completedAt : new Date(task.completedAt);
    console.log(`Completed: ${completedAt.toISOString()}`);
  }

  if (task.dependencies.length > 0) {
    console.log('\nDependencies:');
    details.dependsOn.forEach((dep) => {
      console.log(`  - ${dep.taskId}: ${dep.description} [${dep.status}]`);
    });
  }

  if (details.blockedBy.length > 0) {
    console.log('\n⚠️  Blocked by:');
    details.blockedBy.forEach((dep) => {
      console.log(`  - ${dep.taskId}: ${dep.description} [${dep.status}]`);
    });
  }

  if (details.completion) {
    console.log('\n✅ Completion Artifacts:');
    console.log(`   Files: ${details.completion.files.join(', ')}`);
    if (details.completion.interfaces.length > 0) {
      console.log('   Interfaces:');
      details.completion.interfaces.forEach((iface) => {
        console.log(`     - ${iface.name} (${iface.file})`);
      });
    }
  }

  console.log('');
}

function detectConflicts() {
  const hub = new AgentContextHub();
  const conflicts = hub.detectConflicts();

  console.log('\n🔍 Conflict Detection');
  console.log('='.repeat(50));

  if (conflicts.length === 0) {
    console.log('\n✅ No conflicts detected');
    return;
  }

  console.log(`\n⚠️  ${conflicts.length} conflict(s) found:\n`);

  conflicts.forEach((conflict, index) => {
    console.log(`${index + 1}. [${conflict.severity}] ${conflict.category}`);
    console.log(`   ${conflict.description}`);
    console.log(`   Affected tasks: ${conflict.affectedTasks.join(', ')}`);
    if (conflict.suggestedFix) {
      console.log(`   💡 Suggested fix: ${conflict.suggestedFix}`);
    }
    console.log('');
  });
}

function registerTask(description: string, assignedTo: string, dependencies: string[] = []) {
  const hub = new AgentContextHub();

  const task = hub.registerTask({
    description,
    assignedTo,
    dependencies,
    estimatedContextTokens: 10000,
  });

  console.log(`✅ Task registered: ${task.taskId}`);
  console.log(`   Assigned to: ${task.assignedTo}`);
  console.log(`   Status: ${task.status}`);
  if (dependencies.length > 0) {
    console.log(`   Dependencies: ${dependencies.join(', ')}`);
  }
}

function updateTask(taskId: string, status: string, artifactsJson?: string) {
  const hub = new AgentContextHub();

  const validStatuses: Array<Task['status']> = ['pending', 'in_progress', 'completed', 'blocked'];
  if (!validStatuses.includes(status as Task['status'])) {
    console.error(`❌ Invalid status: ${status}`);
    console.error(`   Valid statuses: ${validStatuses.join(', ')}`);
    process.exit(1);
  }

  const artifacts: Partial<AgentArtifact> | undefined = artifactsJson
    ? (JSON.parse(artifactsJson) as Partial<AgentArtifact>)
    : undefined;

  hub.updateTaskStatus(taskId, status as Task['status'], artifacts);

  console.log(`✅ Task ${taskId} updated to: ${status}`);
  if (artifacts?.files) {
    console.log(`   Files: ${artifacts.files.join(', ')}`);
  }
  if (artifacts?.interfaces) {
    console.log(`   Interfaces: ${artifacts.interfaces.map((i) => i.name).join(', ')}`);
  }
}

function canStart(taskId: string) {
  const hub = new AgentContextHub();
  const result = hub.canStartTask(taskId);

  if (result.canStart) {
    console.log(`✅ Task ${taskId} can start (all dependencies completed)`);
  } else {
    console.log(`⏳ Task ${taskId} is blocked`);
    if (result.blockedBy) {
      console.log(`   Waiting on: ${result.blockedBy.join(', ')}`);
    }
  }
}

// ===========================
// Main CLI Router
// ===========================

async function main() {
  const args = process.argv.slice(2);
  const command = args[0];

  try {
    switch (command) {
      case 'init':
        runInit({ force: args.includes('--force') });
        break;

      case 'update':
        runUpdate();
        break;

      case 'status':
        showStatus();
        break;

      case 'next':
        showNext();
        break;

      case 'detect':
        await detectTech();
        break;

      case 'task':
        if (args.length < 2) {
          console.error('❌ Usage: claude-orchestra task <task-id>');
          process.exit(1);
        }
        showTask(args[1]);
        break;

      case 'conflicts':
        detectConflicts();
        break;

      case 'register':
        if (args.length < 3) {
          console.error(
            '❌ Usage: claude-orchestra register <description> <agent-name> [dependency-ids...]'
          );
          process.exit(1);
        }
        registerTask(args[1], args[2], args.slice(3));
        break;

      case 'update-task':
        if (args.length < 3) {
          console.error(
            '❌ Usage: claude-orchestra update-task <task-id> <status> [artifacts-json]'
          );
          process.exit(1);
        }
        updateTask(args[1], args[2], args[3]);
        break;

      case 'can-start':
        if (args.length < 2) {
          console.error('❌ Usage: claude-orchestra can-start <task-id>');
          process.exit(1);
        }
        canStart(args[1]);
        break;

      case 'help':
      case '--help':
      case '-h':
        showHelp();
        break;

      default:
        if (!command) {
          showHelp();
        } else {
          console.error(`❌ Unknown command: ${command}`);
          console.log('');
          showHelp();
          process.exit(1);
        }
    }
  } catch (error) {
    console.error('❌ Error:', error instanceof Error ? error.message : error);
    process.exit(1);
  }
}

// Run CLI
void main();
